import { StandardEvents } from '@shopify/events';

const QUERY = `#graphql
  query VariantStoreAvailability($id: ID!, $first: Int = 25) {
    node(id: $id) {
      ... on ProductVariant {
        storeAvailability(first: $first) {
          nodes {
            available
            quantityAvailable
            pickUpTime
            location {
              id
              name
              address {
                address1
                city
              }
              metafield(namespace: "availability", key: "store_location") {
                value
              }
            }
          }
        }
      }
    }
  }
`;

class ProductStoreInventory extends HTMLElement {
  static observedAttributes = ['variant-id'];

  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.isLowStock = Number(this.dataset.isLowStock) || 0;
    this.errorMessage = this.dataset.messageNotAvailable || 'In keinem Store verfügbar';
    this.labels = {
      inStock: this.dataset.labelInStock,
      lowStock: this.dataset.labelLowStock,
      outOfStock: this.dataset.labelOutOfStock,
    };
    this.#fetchAvailability(this.getAttribute('variant-id'));
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === null || oldValue === newValue) return;
    this.#fetchAvailability(newValue);
  }

  #handleProductSelect = (event) => {
    if (event.target.closest('product-card')) return;
    const variantId = event.detail?.variantId;
    if (variantId) this.#fetchAvailability(variantId);
  };

  async #fetchAvailability(variantId) {
    if (!variantId || !window.Shopify?.storefrontApiToken) return;
    this.setAttribute('loading', '');
    try {
      const response = await fetch('/api/2026-04/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontApiToken,
        },
        body: JSON.stringify({
          query: QUERY,
          variables: { id: `gid://shopify/ProductVariant/${variantId}` },
        }),
      });
      const { data } = await response.json();
      this.#render(data?.node?.storeAvailability?.nodes ?? []);
    } catch (error) {
      console.error(error);
    } finally {
      this.removeAttribute('loading');
    }
  }

  #status(quantityAvailable) {
    if (quantityAvailable <= 0) return { slug: 'out-of-stock', label: this.labels.outOfStock };
    if (quantityAvailable <= this.isLowStock) return { slug: 'low-stock', label: this.labels.lowStock };
    return { slug: 'in-stock', label: this.labels.inStock };
  }

  #render(stores) {
    this.innerHTML = stores.length
      ? stores
          .map((store) => {
            const { slug, label } = this.#status(store.quantityAvailable);
            const mapsUrl = store.location.metafield?.value;
            const name = mapsUrl
              ? `<a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" class="product-store-inventory__location-link progress-underline progress-underline--bordered">${store.location.name}</a>`
              : `<span class="product-store-inventory__location-link">${store.location.name}</span>`;
            return `
              <div class="product-store-inventory__row">
                ${name}
                <span class="product-store-inventory__status product-store-inventory__status--${slug}">${label}</span>
              </div>
            `;
          })
          .join('')
      : `<p class="product-store-inventory__empty">${this.errorMessage}</p>`;
  }
}

customElements.define('product-store-inventory', ProductStoreInventory);
